import {
  type QueryClient,
  queryOptions,
  useMutation,
  useQuery,
} from "@tanstack/vue-query";
import {
  NovelReadMarker,
  type NovelReadMarkerApplyParams,
  type NovelReadMarkerEntity,
  ReadMarker,
  type ReadMarkerApplyParams,
  type ReadMarkerEntity,
} from "~/src/api";
import cloneDeep from "clone-deep";

export const useNovelReadMarkers = (titleId: MaybeRefOrGetter<string>) => {
  return queryOptions({
    queryKey: ["title_novel_read_markers", "entity", titleId, { authed: true }],
    queryFn: async () =>
      NovelReadMarker.get(toValue(titleId), await getTokenOrThrow()),
    select: (data) => data.attributes.readNovelChapterIds,
    enabled: computed(() => import.meta.client),
    staleTime: useCacheTimings().entity.title_novel_read_markers,
  });
};

export const useNovelReadMarkerManager = (
  queryClient: QueryClient,
  titleId: MaybeRefOrGetter<string>,
) => {
  const queryOptions = useNovelReadMarkers(titleId);
  const markerQuery = useQuery(queryOptions, queryClient);
  const dayjs = useDayjs();

  const markerMutation = useMutation({
    mutationFn: async (
      params: Omit<NovelReadMarkerApplyParams, "titleId"> & {
        noRefresh?: boolean;
      },
    ) => {
      const id = toValue(titleId);
      if (!id || (!params.add?.length && !params.remove?.length)) return;

      await NovelReadMarker.apply(
        {
          titleId: id,
          ...{ ...params, noRefresh: undefined },
        },
        await getTokenOrThrow(),
      );
    },
    onMutate: async (
      params: Omit<NovelReadMarkerApplyParams, "titleId"> & {
        noRefresh?: boolean;
      },
    ) => {
      await queryClient.cancelQueries({ queryKey: queryOptions.queryKey });
      const previousData = queryClient.getQueryData(queryOptions.queryKey);

      const newTimestamp = dayjs(params.time);
      const formatted = newTimestamp.utc().format("YYYY-MM-DDTHH:mm:ss");
      const newMarkers =
        params.add?.map((id) => [id, formatted] as [string, string]) ?? [];
      const removedMarkers = params.remove ?? [];

      queryClient.setQueryData(
        queryOptions.queryKey,
        (oldValue?: NovelReadMarkerEntity): NovelReadMarkerEntity => {
          const base: NovelReadMarkerEntity = oldValue
            ? cloneDeep(oldValue)
            : {
                id: `currUserId_${toValue(titleId)}`,
                type: "title_novel_read_markers",
                attributes: {
                  readNovelChapterIds: {},
                },
                relationships: [],
              };

          const markers = Object.entries(
            base.attributes.readNovelChapterIds,
          ).filter(([id, _]) => !removedMarkers.includes(id));
          base.attributes.readNovelChapterIds = Object.fromEntries(
            markers.concat(newMarkers),
          );

          return base;
        },
      );

      return { previousData };
    },
    onError: (err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryOptions.queryKey, context.previousData);
      }
    },
    onSettled: (data, err, variables) => {
      if (!variables.noRefresh)
        queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
    },
  });

  return {
    markerQuery,
    markerMutation,
    invalidate: () =>
      queryClient.invalidateQueries({ queryKey: queryOptions.queryKey }),
  };
};

// regular chapters
export const useChapterReadMarkers = (titleId: MaybeRefOrGetter<string>) => {
  return queryOptions({
    queryKey: ["title_read_markers", "entity", titleId, { authed: true }],
    queryFn: async () =>
      ReadMarker.get(toValue(titleId), await getTokenOrThrow()),
    select: (data) => data.attributes.readChapterIds,
    enabled: import.meta.client,
    staleTime: useCacheTimings().entity.title_read_markers,
  });
};

export const useChapterReadMarkerManager = (
  queryClient: QueryClient,
  titleId: MaybeRefOrGetter<string>,
) => {
  const queryOptions = useChapterReadMarkers(titleId);
  const markerQuery = useQuery(queryOptions, queryClient);
  const dayjs = useDayjs();

  const markerMutation = useMutation({
    mutationFn: async (
      params: Omit<ReadMarkerApplyParams, "titleId"> & {
        noRefresh?: boolean;
      },
    ) => {
      const id = toValue(titleId);
      if (!id || (!params.add?.length && !params.remove?.length)) return;

      await ReadMarker.apply(
        {
          titleId: id,
          ...{ ...params, noRefresh: undefined },
        },
        await getTokenOrThrow(),
      );
    },
    onMutate: async (
      params: Omit<ReadMarkerApplyParams, "titleId"> & {
        noRefresh?: boolean;
      },
    ) => {
      await queryClient.cancelQueries({ queryKey: queryOptions.queryKey });
      const previousData = queryClient.getQueryData(queryOptions.queryKey);

      const newTimestamp = dayjs(params.time);
      const formatted = newTimestamp.utc().format("YYYY-MM-DDTHH:mm:ss");
      const newMarkers: [string, string][] =
        params.add?.map((id) => [id, formatted]) ?? [];
      const removedMarkers = params.remove ?? [];

      queryClient.setQueryData(
        queryOptions.queryKey,
        (oldValue?: ReadMarkerEntity): ReadMarkerEntity => {
          const base: ReadMarkerEntity = oldValue
            ? cloneDeep(oldValue)
            : {
                id: `currUserId_${toValue(titleId)}`,
                type: "title_read_markers",
                attributes: {
                  readChapterIds: {},
                },
                relationships: [],
              };

          const markers = Object.entries(base.attributes.readChapterIds).filter(
            ([id, _]) => !removedMarkers.includes(id),
          );
          base.attributes.readChapterIds = Object.fromEntries(
            markers.concat(newMarkers),
          );

          return base;
        },
      );

      return { previousData };
    },
    onError: (err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryOptions.queryKey, context.previousData);
      }
    },
    onSettled: (data, err, variables) => {
      if (!variables.noRefresh)
        queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
    },
  });

  return {
    markerQuery,
    markerMutation,
    invalidate: () =>
      queryClient.invalidateQueries({ queryKey: queryOptions.queryKey }),
  };
};
